import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Normalize destination names for consistent comparison and storage.
 * - Lowercase
 * - Trim whitespace
 * - Remove duplicate spaces
 * - Strip diacritics
 * - Remove bracketed suffixes like "(NL)"
 * - Normalize punctuation to comma/space
 * - Map common synonyms/abbreviations
 */
export function normalizeDestinationName(name: string): string {
  let normalized = name
    .toLowerCase()
    .trim()
    // Remove diacritics
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    // Remove bracketed suffixes
    .replace(/\s*\([^)]*\)\s*/g, ' ')
    // Collapse spaces
    .replace(/\s+/g, ' ')
    // Keep only alphanumeric, spaces, and commas
    .replace(/[^\w\s,]/g, '')
    .trim();

  // Map common synonyms/abbreviations
  const synonyms: Record<string, string> = {
    'us': 'united states',
    'usa': 'united states',
    'uk': 'united kingdom',
    'england': 'united kingdom',
    'scotland': 'united kingdom',
    'wales': 'united kingdom',
    'northern ireland': 'united kingdom',
    'uae': 'united arab emirates',
    'nl': 'netherlands',
  };

  // Replace full-word matches
  for (const [abbrev, full] of Object.entries(synonyms)) {
    const regex = new RegExp(`\\b${abbrev}\\b`, 'g');
    normalized = normalized.replace(regex, full);
  }

  return normalized;
}

// Map of departure cities to their countries (for common UK/EU cities)
const CITY_COUNTRY_MAP: Record<string, string> = {
  'london': 'united kingdom',
  'manchester': 'united kingdom',
  'birmingham': 'united kingdom',
  'edinburgh': 'united kingdom',
  'glasgow': 'united kingdom',
  'bristol': 'united kingdom',
  'cardiff': 'united kingdom',
  'belfast': 'united kingdom',
  'liverpool': 'united kingdom',
  'leeds': 'united kingdom',
  'newcastle': 'united kingdom',
  'nottingham': 'united kingdom',
  'sheffield': 'united kingdom',
  'southampton': 'united kingdom',
  'paris': 'france',
  'lyon': 'france',
  'marseille': 'france',
  'nice': 'france',
  'berlin': 'germany',
  'munich': 'germany',
  'frankfurt': 'germany',
  'hamburg': 'germany',
  'amsterdam': 'netherlands',
  'rotterdam': 'netherlands',
  'dublin': 'ireland',
  'cork': 'ireland',
  'madrid': 'spain',
  'barcelona': 'spain',
  'rome': 'italy',
  'milan': 'italy',
  'brussels': 'belgium',
  'vienna': 'austria',
  'zurich': 'switzerland',
  'geneva': 'switzerland',
  'lisbon': 'portugal',
  'porto': 'portugal',
  'copenhagen': 'denmark',
  'stockholm': 'sweden',
  'oslo': 'norway',
  'helsinki': 'finland',
  'warsaw': 'poland',
  'prague': 'czech republic',
  'budapest': 'hungary',
  'athens': 'greece',
};

// Regional pairs where train/ferry is a viable alternative to flying
const REGIONAL_PAIRS = [
  ['united kingdom', 'ireland'],
  ['united kingdom', 'france'],
  ['united kingdom', 'netherlands'],
  ['united kingdom', 'belgium'],
  ['france', 'belgium'],
  ['france', 'germany'],
  ['france', 'switzerland'],
  ['france', 'spain'],
  ['france', 'italy'],
  ['germany', 'netherlands'],
  ['germany', 'belgium'],
  ['germany', 'austria'],
  ['germany', 'switzerland'],
  ['germany', 'czech republic'],
  ['germany', 'poland'],
  ['germany', 'denmark'],
  ['italy', 'switzerland'],
  ['italy', 'austria'],
  ['spain', 'portugal'],
  ['netherlands', 'belgium'],
  ['austria', 'czech republic'],
  ['austria', 'hungary'],
  ['sweden', 'denmark'],
  ['sweden', 'norway'],
  ['norway', 'denmark'],
];

/**
 * Detect if a destination is domestic (same country) or regional (neighboring country)
 * where car/train/ferry might be a better option than flying.
 */
export function isDomesticOrRegional(
  departureCity: string, 
  destinationName: string
): { isDomestic: boolean; isRegional: boolean; suggestion: string | null } {
  const departureLower = departureCity.toLowerCase().trim();
  const departureCountry = CITY_COUNTRY_MAP[departureLower] || null;
  
  if (!departureCountry) {
    return { isDomestic: false, isRegional: false, suggestion: null };
  }
  
  // Extract country from destination (format: "City, Country")
  const destinationParts = destinationName.split(',');
  const destinationCountryRaw = destinationParts[destinationParts.length - 1]?.trim() || '';
  const destinationCountry = normalizeDestinationName(destinationCountryRaw);
  
  // Check if same country (domestic)
  if (destinationCountry === departureCountry) {
    return {
      isDomestic: true,
      isRegional: false,
      suggestion: '🚗 Consider driving or taking the train — often cheaper & more convenient for domestic trips!'
    };
  }
  
  // Check if neighboring/regional
  const isRegional = REGIONAL_PAIRS.some(
    pair => (pair[0] === departureCountry && pair[1] === destinationCountry) ||
            (pair[1] === departureCountry && pair[0] === destinationCountry)
  );
  
  if (isRegional) {
    return {
      isDomestic: false,
      isRegional: true,
      suggestion: '🚆 Train or ferry might be a great option for this nearby destination!'
    };
  }
  
  return { isDomestic: false, isRegional: false, suggestion: null };
}
